import React from 'react';
import GlassOverlay from '../common/GlassOverlay';
import { ReservationThemeTokens } from '../../../core/ui/reservationTheme';

interface PublicReservationLayoutProps {
  theme: ReservationThemeTokens;
  isMinimalGlassTheme?: boolean;
  header: React.ReactNode;
  body: React.ReactNode;
  footer?: React.ReactNode;
  topRightContent?: React.ReactNode;
  watermarkText?: string;
  decorations?: React.ReactNode;
  cardPreviewZone?: string;
  cardPreviewZoneClassName?: string;
  cardPreviewZoneProps?: Omit<React.HTMLAttributes<HTMLDivElement>, 'className'>;
  compactBody?: boolean;
}

const PublicReservationLayout: React.FC<PublicReservationLayoutProps> = ({
  theme,
  isMinimalGlassTheme = false,
  header,
  body,
  footer,
  topRightContent,
  watermarkText,
  decorations,
  cardPreviewZone,
  cardPreviewZoneClassName = '',
  cardPreviewZoneProps,
  compactBody = false,
}) => {
  const wrapperClassName = `relative min-h-[100dvh] w-full overflow-x-hidden overflow-y-auto flex flex-col ${theme.styles.page}`;

  const cardContent = (
    <div
      {...(cardPreviewZone ? { ...cardPreviewZoneProps, 'data-preview-zone': cardPreviewZone } : {})}
      className={`relative flex flex-col w-full gap-3 px-3 py-4 md:gap-4 md:px-8 md:py-8 min-h-0 overflow-visible ${theme.styles.card} ${cardPreviewZoneClassName}`.trim()}
      style={{ ...(theme.cardStyle || {}), ...(cardPreviewZone ? cardPreviewZoneProps?.style : {}) }}
    >
      {topRightContent && (
        <div className="static mb-2 flex w-full justify-end gap-2 text-sm font-medium md:absolute md:right-4 md:top-4 md:mb-0 md:w-auto">
          {topRightContent}
        </div>
      )}
      <div className={`${compactBody ? 'flex flex-col min-h-0' : 'flex flex-col flex-1 min-h-0'} gap-3 md:gap-4`}>
        <div className="flex-shrink-0 flex flex-col gap-3 text-center items-center">
          {header}
        </div>
        <div className={compactBody ? 'min-h-0 overflow-visible overscroll-contain' : 'flex-1 min-h-0 overflow-visible overscroll-contain'}>
          {body}
        </div>
        {footer && (
          <div className="flex-shrink-0 pt-2 pb-[calc(env(safe-area-inset-bottom)+12px)]">{footer}</div>
        )}
      </div>
      {watermarkText && (
        <div
          className={`pointer-events-none hidden self-end pt-2 text-xs drop-shadow md:block md:text-sm ${theme.styles.watermark || ''}`}
          style={{
            color: theme.watermarkStyle?.color || theme.colors.textSecondary,
            ...(theme.watermarkStyle || {}),
          }}
        >
          {watermarkText}
        </div>
      )}
    </div>
  );

  return (
    <div
      className={wrapperClassName}
      style={{
        color: 'var(--color-text-primary)',
        ...(theme.pageStyle || {}),
      }}
    >
      {theme.styles.pageOverlay && (
        <div
          className={`pointer-events-none absolute inset-0 ${theme.styles.pageOverlay}`}
          aria-hidden
        />
      )}
      {decorations && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          {decorations}
        </div>
      )}
      <div className={`relative z-10 flex-1 flex flex-col items-center ${theme.styles.pageInner}`}>
        <div className="w-full max-w-5xl px-4 py-8 md:py-12">
          {isMinimalGlassTheme ? (
            <GlassOverlay>{cardContent}</GlassOverlay>
          ) : (
            cardContent
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicReservationLayout;
